import { createContext, useCallback, useContext, useEffect, useId, useMemo, useState } from 'react';

export interface InvalidInputsApi {
  report: (id: string, invalid: boolean) => void;
}

/** Окно отдаёт сюда свой приёмник — поля ввода сообщают ему, что держат неверное значение. */
export const InvalidInputsContext = createContext<InvalidInputsApi | null>(null);

/** Одна подпись на все окна: почему «Сохранить» и «Провести» недоступны. */
export const INVALID_INPUTS_HINT = 'Исправьте поля, выделенные красным, — пока они не исправлены, сохранить нельзя';

/** То же коротко — для подсказки на недоступной кнопке. */
export const INVALID_INPUTS_REASON = 'Исправьте поля, выделенные красным';

/**
 * Для окна с полями ввода. `hasInvalid` — в окне есть поле с неверным значением (оно выделено
 * красным и объясняет причину). Такое поле отдаёт наружу «пусто», а для необязательного поля
 * («Оплачено», оптовая цена) это неотличимо от «не заполнено»: без этого признака окно
 * сохранилось бы, молча отбросив введённое.
 */
export function useInvalidInputs(): { hasInvalid: boolean; api: InvalidInputsApi } {
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set());
  const report = useCallback((id: string, invalid: boolean) => {
    setIds((prev) => {
      if (prev.has(id) === invalid) return prev;
      const next = new Set(prev);
      if (invalid) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  const api = useMemo(() => ({ report }), [report]);
  return { hasInvalid: ids.size > 0, api };
}

/** Для поля ввода: сообщить окну о неверном значении. Исчезнувшее поле (удалённая строка) свою
 * отметку снимает. Вне окна с приёмником ничего не делает. */
export function useReportInvalid(invalid: boolean): void {
  const api = useContext(InvalidInputsContext);
  const id = useId();
  useEffect(() => {
    if (!api) return undefined;
    api.report(id, invalid);
    return () => api.report(id, false);
  }, [api, id, invalid]);
}
